// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

/**
 * An address an iframe may load: an absolute http(s) URL, or a path on this
 * origin (the ingress proxies services under paths such as /comfyui/). The
 * value comes from the environment or from the browser's own storage, so
 * schemes like javascript: and data:, network-path references (//host) and
 * anything that resolves to another origin are refused.
 */
export function safeFrameUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (!v) return null;
  // Whitespace and control characters inside the address, and "//host".
  if (/[\s\u0000-\u001f]/.test(v) || v.startsWith('//')) return null;
  if (v.startsWith('/')) {
    try {
      const sameOrigin =
        new URL(v, window.location.origin).origin === window.location.origin;
      return sameOrigin ? v : null;
    } catch {
      return null;
    }
  }
  try {
    const url = new URL(v);
    // Returned as written: only the scheme matters here.
    return url.protocol === 'http:' || url.protocol === 'https:' ? v : null;
  } catch {
    return null;
  }
}
